/**
 * 要点组句练习组件 - Brutalist 风格
 */

import React, { useState, useEffect, useMemo } from 'react';
import type { ArgumentCard } from './types';
import { evaluateArgument, EvaluationResult } from './evaluateService';
import './TranslationPractice.css'; // 复用翻译练习的样式

interface Props {
  card: ArgumentCard;
  onEvaluated: (evaluated: boolean) => void;
}

// 随机选择最多 n 个元素
function pickRandom<T>(arr: T[], n: number): T[] {
  if (arr.length <= n) return arr;
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, n);
}

export function ArgumentPractice({ card, onEvaluated }: Props) {
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [result, setResult] = useState<EvaluationResult | null>(null);
  const [showReference, setShowReference] = useState(false);

  // 随机选择最多3个要点
  const displayPoints = useMemo(() => pickRandom(card.keyPoints, 3), [card.id]);

  // 通知父组件评判状态
  useEffect(() => {
    onEvaluated(submitted && !evaluating);
  }, [submitted, evaluating, onEvaluated]);

  // 卡片切换时重置状态
  useEffect(() => {
    setAnswer('');
    setSubmitted(false);
    setResult(null);
    setShowReference(false);
  }, [card.id]);

  const handleSubmit = async () => {
    if (!answer.trim() || evaluating) return;
    
    setEvaluating(true);
    setSubmitted(true);
    
    try {
      const evalResult = await evaluateArgument(card, answer);
      setResult(evalResult);
    } catch (err) {
      console.error('评判失败:', err);
      setResult({ score: 0, feedback: '评判服务暂时不可用' });
    } finally {
      setEvaluating(false);
    }
  };

  return (
    <div className="translation-practice">
      {/* 观点区块 */}
      <div className="translation-question-block">
        <p className="argument-label">观点</p>
        <p className="translation-question">{card.argument}</p>
      </div>

      {/* 要点区块 */}
      <div className="argument-points-block">
        <p className="argument-label">用以下要点展开（2-3句）</p>
        <div className="translation-keywords">
          {displayPoints.map((point, i) => (
            <span key={i} className="translation-keyword">{point}</span>
          ))}
        </div>
      </div>

      {/* 输入区域 */}
      <div className="translation-input-section">
        <div className="translation-input-row">
          <textarea
            id={`argument-answer-${card.id}`}
            className="translation-textarea"
            placeholder="用上面的要点写 2-3 句英文，表达这个观点..."
            rows={4}
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={submitted}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          {!submitted && (
            <button 
              className="translation-submit-btn" 
              type="button" 
              onClick={handleSubmit}
              disabled={!answer.trim()}
            >
              判分
            </button>
          )}
        </div>
      </div>

      {/* 评判中 */}
      {submitted && evaluating && (
        <div className="translation-loading">
          <div className="loading-spinner"><span></span></div>
          <p>AI 评判中...</p>
        </div>
      )}

      {/* 评判结果 */}
      {submitted && !evaluating && result && (
        <div className="translation-result">
          {/* 得分 */}
          <div className="translation-score-row">
            <div className="translation-score-bar">
              <div 
                className="translation-score-fill" 
                style={{ width: `${result.score}%` }} 
              />
            </div>
            <span className="translation-score-value">{result.score}</span>
          </div>

          {/* AI 反馈 */}
          <div className="translation-feedback">
            <p className="translation-feedback-text">{result.feedback}</p>
            {result.keywordUsed && result.keywordUsed.length > 0 && (
              <p className="translation-keywords-used">
                使用了: {result.keywordUsed.join(', ')}
              </p>
            )}
          </div>

          {/* 修改建议 */}
          {result.corrections && (
            <div className="translation-corrections">
              <p className="translation-corrections-label">建议</p>
              <p className="translation-corrections-text">{result.corrections}</p>
            </div>
          )}

          {/* 参考答案 */}
          <button
            type="button"
            className="translation-toggle-btn"
            onClick={() => setShowReference(!showReference)}
          >
            {showReference ? '隐藏参考答案' : '显示参考答案'}
          </button>

          {showReference && (
            <div className="translation-reference">
              <p className="translation-reference-label">参考答案</p>
              <p className="translation-reference-text">{card.reference}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
